<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <button v-if="isSuperAdmin" @click="openCreateModal" class="btn btn-primary btn-sm">
          <vue-feather type="plus" size="14" class="me-1" />
          Add User
        </button>
      </template>
    </breadcrumb-index>

    <DataTable
      :items="users"
      :columns="columns"
      :loading="loading"
      search-placeholder="Search users..."
      empty-icon="users"
      empty-title="No users found"
      empty-description="Add your first user to get started"
    >
      <template #filters>
        <select v-model="filterRole" class="filter-select-modern">
          <option value="">All Roles</option>
          <option value="super_admin">Super Admin</option>
          <option value="admin">Admin</option>
          <option value="content_manager">Content Manager</option>
          <option value="reviewer">Reviewer</option>
        </select>
        <select v-model="filterStatus" class="filter-select-modern">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </template>

      <template #cell-user="{ item }">
        <div class="table-avatar">
          <img v-if="item.avatar" :src="item.avatar" class="avatar-img" alt="" />
          <div v-else class="avatar-placeholder">{{ getInitials(item.fullName) }}</div>
          <div class="avatar-info">
            <span class="avatar-name">{{ item.fullName }}</span>
            <span class="avatar-subtitle">{{ item.email }}</span>
          </div>
        </div>
      </template>

      <template #cell-role="{ item }">
        <span :class="['badge', getRoleClass(item.role)]">{{ formatRole(item.role) }}</span>
      </template>

      <template #cell-status="{ item }">
        <span :class="['status-badge', item.isActive ? 'status-active' : 'status-inactive']">
          {{ item.isActive ? 'Active' : 'Inactive' }}
        </span>
      </template>

      <template #cell-twoFactor="{ item }">
        <vue-feather
          :type="item.is2faEnabled ? 'shield' : 'shield-off'"
          :class="item.is2faEnabled ? 'text-success' : 'text-muted'"
          size="18"
        />
      </template>

      <template #cell-lastLogin="{ item }">
        <span class="text-muted">{{ formatDate(item.createdAt) }}</span>
      </template>

      <template #cell-actions="{ item }">
        <div class="action-buttons">
          <button 
            v-if="canEdit(item)"
            class="action-btn action-edit" 
            @click="openEditModal(item)"
            v-tooltip="'Edit user'"
          >
            <vue-feather type="edit-2" size="14" />
          </button>
          <button 
            v-if="canDelete(item)" 
            class="action-btn action-delete" 
            @click="confirmDelete(item)" 
            v-tooltip="{ text: 'Delete user', variant: 'danger' }"
          >
            <vue-feather type="trash-2" size="14" />
          </button>
        </div>
      </template>

      <template #empty-action>
        <button @click="openCreateModal" class="btn btn-primary">
          <vue-feather type="plus" size="16" class="me-1" />
          Add First User
        </button>
      </template>
    </DataTable>

    <!-- Create/Edit Modal -->
    <CreateModal
      v-model="showModal"
      :title="isEditing ? 'Edit User' : 'Add User'"
      icon="user"
      size="lg"
      :loading="saving"
      :submit-text="isEditing ? 'Update' : 'Create'"
      :submit-disabled="!form.fullName || !form.email || (!isEditing && !form.password)"
      :item-id="isEditing ? editingId : ''"
      @submit="saveUser"
    >
      <form @submit.prevent="saveUser" class="modal-form">
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Full Name <span class="text-danger">*</span></label>
            <input 
              v-model="form.fullName" 
              type="text" 
              class="form-control" 
              placeholder="e.g., John Doe"
              required
            />
          </div>

          <div class="form-group">
            <label class="form-label">Email <span class="text-danger">*</span></label>
            <input 
              v-model="form.email" 
              type="email" 
              class="form-control" 
              placeholder="user@example.com"
              required
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">
              Password 
              <span v-if="!isEditing" class="text-danger">*</span>
              <span v-else class="text-muted">(leave blank to keep current)</span>
            </label>
            <input 
              v-model="form.password" 
              type="password" 
              class="form-control" 
              placeholder="••••••••"
              :required="!isEditing"
              minlength="8"
            />
          </div>

          <div class="form-group">
            <label class="form-label">
              Confirm Password
              <span v-if="!isEditing" class="text-danger">*</span>
            </label>
            <input 
              v-model="form.passwordConfirmation" 
              type="password" 
              class="form-control" 
              placeholder="••••••••"
              :required="!isEditing && form.password"
            />
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Role <span class="text-danger">*</span></label>
          <select v-model="form.role" class="form-control" required>
            <option value="admin">Admin</option>
            <option value="content_manager">Content Manager</option>
            <option value="reviewer">Reviewer</option>
          </select>
          <small class="text-muted">Note: Super Admin can only be created via CLI</small>
        </div>

        <div v-if="formError" class="alert alert-danger">{{ formError }}</div>
      </form>
    </CreateModal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'
import { useNotifications } from '@/composables/useNotifications'
import DataTable from '@/components/data-table/DataTable.vue'
import CreateModal from '@/components/modals/CreateModal.vue'

const API_BASE = '/v1/admin'

export default {
  name: 'UsersList',
  components: { DataTable, CreateModal },
  setup() {
    const authStore = useAuthStore()
    const { toast, confirmDelete: confirmDeleteAction } = useNotifications()
    
    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Users', active: true }
    ]

    const isSuperAdmin = computed(() => authStore.isSuperAdmin)

    const columns = [
      { key: 'user', label: 'User', sortable: true, minWidth: '250px' },
      { key: 'role', label: 'Role', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'twoFactor', label: '2FA', class: 'text-center' },
      { key: 'lastLogin', label: 'Last Login', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    const users = ref([])
    const loading = ref(true)
    const filterRole = ref('')
    const filterStatus = ref('')

    // Modal state
    const showModal = ref(false)
    const isEditing = ref(false)
    const saving = ref(false)
    const editingId = ref(null)
    const formError = ref('')
    const form = reactive({
      fullName: '',
      email: '',
      password: '',
      passwordConfirmation: '',
      role: 'content_manager'
    })

    const filteredUsers = computed(() => {
      return users.value.filter(u => {
        const matchesRole = !filterRole.value || u.role === filterRole.value
        const matchesStatus = !filterStatus.value || (filterStatus.value === 'active' ? u.isActive : !u.isActive)
        return matchesRole && matchesStatus
      })
    })

    const fetchUsers = async () => {
      loading.value = true
      try {
        const response = await api.get(`${API_BASE}/admin-users`)
        users.value = response.data
      } catch (error) {
        console.error('Failed to fetch users:', error)
        toast.error('Failed to load users')
      } finally {
        loading.value = false
      }
    }

    const getInitials = (name) => {
      if (!name) return '??'
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    }

    const getRoleClass = (role) => {
      const classes = {
        super_admin: 'bg-danger',
        admin: 'bg-primary',
        content_manager: 'bg-info',
        reviewer: 'bg-warning'
      }
      return classes[role] || 'bg-secondary'
    }

    const formatRole = (role) => {
      if (!role) return ''
      return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    const formatDate = (date) => {
      if (!date) return 'Never'
      return new Date(date).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      })
    }

    const canDelete = (user) => {
      return authStore.isSuperAdmin && user.role !== 'super_admin' && user.id !== authStore.user?.id
    }

    const canEdit = (user) => {
      // Only super_admin can edit admin users
      return authStore.isSuperAdmin
    }

    const resetForm = () => {
      form.fullName = ''
      form.email = ''
      form.password = ''
      form.passwordConfirmation = ''
      form.role = 'content_manager'
      editingId.value = null
      isEditing.value = false
      formError.value = ''
    }

    const openCreateModal = () => {
      resetForm()
      showModal.value = true
    }

    const openEditModal = (user) => {
      isEditing.value = true
      editingId.value = user.id
      form.fullName = user.fullName || ''
      form.email = user.email || ''
      form.password = ''
      form.passwordConfirmation = ''
      form.role = user.role || 'content_manager'
      formError.value = ''
      showModal.value = true
    }

    const saveUser = async () => {
      formError.value = ''

      if (!form.fullName || !form.email) {
        formError.value = 'Full name and email are required'
        return
      }
      
      // Validate password for new users
      if (!isEditing.value && !form.password) {
        formError.value = 'Password is required'
        return
      }

      // Validate password confirmation
      if (form.password && form.password !== form.passwordConfirmation) {
        formError.value = 'Passwords do not match'
        return
      }

      saving.value = true
      
      try {
        const payload = {
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          role: form.role
        }

        // Only include password if provided
        if (form.password) {
          payload.password = form.password
        }

        if (isEditing.value) {
          payload.createdBy = authStore.user?.id
          const response = await api.put(`${API_BASE}/admin-users/${editingId.value}`, payload)
          const index = users.value.findIndex(u => u.id === editingId.value)
          if (index !== -1) {
            users.value[index] = { ...users.value[index], ...(response.data || payload) }
          }
          toast.success('User updated successfully')
        } else {
          payload.createdBy = authStore.user?.id
          const response = await api.post(`${API_BASE}/admin-users`, payload)
          users.value.unshift(response.data?.user || response.data)
          toast.success('User created successfully')
        }
        showModal.value = false
        resetForm()
      } catch (error) {
        formError.value = error.response?.data?.error || error.response?.data?.message || 'Failed to save user'
      } finally {
        saving.value = false
      }
    }

    const confirmDelete = async (user) => {
      const confirmed = await confirmDeleteAction(user.fullName, 'This action cannot be undone.')
      if (confirmed) {
        try {
          await api.delete(`${API_BASE}/admin-users/${user.id}`)
          users.value = users.value.filter(u => u.id !== user.id)
          toast.success(`"${user.fullName}" has been deleted`)
        } catch (error) {
          toast.error(error.response?.data?.error || 'Failed to delete user')
        }
      }
    }

    onMounted(fetchUsers)

    return {
      breadcrumbs,
      columns,
      users: filteredUsers,
      loading,
      filterRole,
      filterStatus,
      showModal,
      isEditing,
      editingId,
      saving,
      form,
      formError,
      isSuperAdmin,
      openCreateModal,
      openEditModal,
      saveUser,
      getInitials,
      getRoleClass,
      formatRole,
      formatDate,
      canEdit,
      canDelete,
      confirmDelete
    }
  }
}
</script>

<style scoped>
.modal-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-control {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

:global(body.dark-mode) .form-label {
  color: #d1d5db;
}

:global(body.dark-mode) .form-control {
  background: #1e293b;
  border-color: #374151;
  color: #f3f4f6;
}
</style>
